import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { Box, Checkbox, Chip, CircularProgress, Collapse, IconButton, Paper, Stack, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CachedIcon from '@mui/icons-material/Cached';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import ImageCarousel from 'components/modals/ImageCarousel/ImageCarousel';
import { colors } from 'styles/colors';
import {
  LeafCategoryOption,
  RawProductIdentityPatch,
  RawProductDescriptionPatch,
  RawProductUsageEdit,
  RawProductContentsPatch,
  RawProductWarnings,
  RawProductAttributes,
  RawProductAttributeName,
  RawProductShade,
  RawProductScent,
  RawProductInfoPatch,
  ReextractRawProductBrandResult,
  BrandOption,
  RawProductBrandPatch,
  RawProductReadiness,
  RawProductContentEntry,
  RawProductContentsLanguage,
  RawProductReviewItem,
  RawProductStep,
  RedescribeRawProductResult,
  ReextractRawProductContentsResult,
  ReextractRawProductUsagesResult,
  ReextractRawProductIngredientsResult,
  ReextractRawProductWarningsResult,
  ReextractRawProductAttributesResult,
  AttributePart,
  reextractRawProductAttributePart,
  RankRawProductIngredientsResult,
  RawProductIngredient,
  RawProductUsage,
  RerunRawProductStepResult,
  UpdateRawProductIdentityResult,
  UpdateRawProductInfoResult,
} from 'services/rawProductService';
import { CountryOption, ProductIdentityValues } from 'services/lookupsService';
import { IPhotoSize } from 'types/types';
import IdentityFieldModal, { IdentityFieldName } from './IdentityFieldModal';
import InfoFieldModal, { INFO_FIELDS, InfoFieldName } from './InfoFieldModal';
import CategoryPickerModal from './CategoryPickerModal';
import BrandModal from './BrandModal';
import DescriptionModal from './DescriptionModal';
import UsageModal from './UsageModal';
import ContentsModal from './ContentsModal';
import WarningsModal from './WarningsModal';
import IngredientsModal from './IngredientsModal';
import AttributesModal, { AttributeSection } from './AttributesModal';
import CustomDialog from 'components/dialogs/CustomDialog';
import { copyToClipboard } from 'common/utils';
import { snackbarService } from 'services/snackbarService';

interface Props {
  item: RawProductReviewItem;
  leafCategories: LeafCategoryOption[];
  isConfirming: boolean;
  isPromoting: boolean;
  isRunningMissing: boolean;
  isReextracting: boolean;
  isDeletingSuggestion: boolean;
  isRedescribing: boolean;
  isSavingIdentity: boolean;
  isSavingInfo: boolean;
  isReextractingInfo: boolean;
  isReextractingBrand: boolean;
  brandOptions: BrandOption[];
  isEditingBrand: boolean;
  isSavingBrand: boolean;
  onEditBrand: (rawProductId: string) => void;
  onCloseBrandEditor: () => void;
  onSaveBrand: (rawProductId: string, patch: RawProductBrandPatch) => void;
  isReextractingContents: boolean;
  isReextractingUsages: boolean;
  isSavingIngredients: boolean;
  isReextractingWarnings: boolean;
  isReextractingAttributes: boolean;
  isRankingIngredients: boolean;
  isRerunningPipeline: boolean;
  // Which editor is open on this card, or null. Held by the page rather than
  // here: it is the page that hears a save succeed, and an editor has to close
  // on that rather than on the click that started it.
  editingField: IdentityFieldName | null;
  editingInfoField: InfoFieldName | null;
  isEditingCategory: boolean;
  identityValues?: ProductIdentityValues;
  countries?: CountryOption[];
  // What a save on this card stored, which the page holds rather than refetching
  // the queue. Undefined until one has been made; after that it is what the row
  // holds and wins over the values the list was loaded with.
  // Either a save on this card or a pass run again from it - the page keeps
  // both in one slot, and a re-run carries the leaf it picked while a save
  // does not.
  identity?: UpdateRawProductIdentityResult | RerunRawProductStepResult;
  info?: UpdateRawProductInfoResult;
  brand?: ReextractRawProductBrandResult;
  description?: RedescribeRawProductResult;
  contents?: ReextractRawProductContentsResult;
  usages?: ReextractRawProductUsagesResult;
  ingredients?: ReextractRawProductIngredientsResult;
  rankedIngredients?: RankRawProductIngredientsResult;
  warnings?: ReextractRawProductWarningsResult;
  attributes?: ReextractRawProductAttributesResult;
  // What the row is waiting for as of the last write to it, whichever write that
  // was. One slot rather than reading it off each answer in turn: every answer
  // carries the whole picture as it stood when it was computed, so the newest is
  // the only one worth reading and the page keeps just that.
  readiness?: RawProductReadiness;
  // The front photo as of the last save, for the same reason: the strip is
  // marked from this rather than from the payload the page was loaded with.
  avatarMediaId?: string | null;
  isSettingAvatar: boolean;
  // Which pass is running for this card, if any. One at a time: they write to
  // the same row and the later ones read what the earlier ones wrote, so two in
  // flight at once would be a race over the same columns.
  rerunningStep?: RawProductStep | null;
  onConfirmCategory: (rawProductId: string, leafCategoryId: string) => void;
  onPromote: (rawProductId: string) => void;
  // Every section not read yet, in one press.
  onRunMissing: (rawProductId: string, withContents: boolean) => void;
  onReextractOcr: (rawProductId: string) => void;
  onRedescribe: (rawProductId: string) => void;
  onEditDescription: (rawProductId: string) => void;
  isEditingDescription: boolean;
  isSavingDescription: boolean;
  onCloseDescriptionEditor: () => void;
  onSaveDescription: (rawProductId: string, patch: RawProductDescriptionPatch) => void;
  isSavingUsages: boolean;
  onSaveUsages: (rawProductId: string, usages: RawProductUsageEdit[]) => void;
  isSavingContents: boolean;
  onSaveContents: (rawProductId: string, contents: RawProductContentsPatch) => void;
  isSavingWarnings: boolean;
  onSaveWarnings: (rawProductId: string, warnings: RawProductWarnings) => void;
  isSavingAttributes: boolean;
  onSaveAttributes: (rawProductId: string, attributes: RawProductAttributes) => void;
  onEditField: (rawProductId: string, field: IdentityFieldName) => void;
  onCloseFieldEditor: () => void;
  onSaveIdentity: (rawProductId: string, patch: RawProductIdentityPatch) => void;
  onEditInfoField: (rawProductId: string, field: InfoFieldName) => void;
  onCloseInfoFieldEditor: () => void;
  onSaveInfo: (rawProductId: string, patch: RawProductInfoPatch) => void;
  onReextractInfo: (rawProductId: string) => void;
  onReextractBrand: (rawProductId: string) => void;
  // The photos to read go with the request: nothing stores them, because which
  // photos show the box is a judgement about this reading rather than a fact
  // about the pack.
  onReextractContents: (rawProductId: string, mediaIds: string[]) => void;
  onReextractUsages: (rawProductId: string, mediaIds: string[]) => void;
  onSaveIngredients: (rawProductId: string, ingredients: RawProductIngredient[]) => void;
  onReextractWarnings: (rawProductId: string) => void;
  onReextractAttributes: (rawProductId: string, withDescription: boolean) => void;
  // No photos here: the ranking reads the stored panel and never the label.
  onRankIngredients: (rawProductId: string) => void;
  onRerunPipeline: (rawProductId: string) => void;
  onEditCategory: (rawProductId: string) => void;
  onCloseCategoryEditor: () => void;
  onDeleteSuggestion: (suggestionId: string) => void;
  onDeleteImage: (suggestionId: string, mediaId: string) => void;
  onSetAvatar: (rawProductId: string, mediaId: string) => void;
  onRerunStep: (rawProductId: string, step: RawProductStep) => void;
}

// Each field takes an equal share of the row and grows with it, so they span the
// card instead of crowding into its left edge. A minimum width keeps a long
// value from squeezing its neighbours to nothing before the row wraps.
//
// A field the reviewer can correct carries its own pencil, beside its own label,
// because that is where the wrong value is being looked at. The value itself
// stays plain text: the row is read far more often than it is edited.
function Field({
  label,
  value,
  onEdit,
  onRerun,
  isRerunning,
  rtl,
}: {
  label: string;
  value: string | null;
  onEdit?: () => void;
  // Asks the pass that wrote this field to read the pack again. Beside the
  // pencil rather than instead of it, because they answer different questions: a
  // reviewer who knows the right value types it, and one who only knows this one
  // is wrong asks for another reading.
  onRerun?: () => void;
  isRerunning?: boolean;
  // Arabic values read the other way within themselves, so the run itself is
  // laid out right-to-left. The block still starts at the left edge like every
  // other value on the card: a column whose values are pushed to its far side
  // cannot be read down against its neighbours.
  rtl?: boolean;
}) {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.25} alignItems="center">
        <CustomText text={label} fontSize={13} color={colors.gray} />
        {onEdit && (
          <Tooltip title={`Edit ${label.toLowerCase()}`}>
            <IconButton size="small" onClick={onEdit} sx={{ padding: '1px' }}>
              <EditOutlinedIcon sx={{ fontSize: 14, color: colors.gray }} />
            </IconButton>
          </Tooltip>
        )}
        {onRerun && (
          <Tooltip title={`Read ${label.toLowerCase()} off the pack again`}>
            <Box sx={{ display: 'inline-flex' }}>
              <IconButton
                size="small"
                disabled={isRerunning}
                onClick={onRerun}
                sx={{ padding: '1px' }}
              >
                {isRerunning ? (
                  <CircularProgress size={12} />
                ) : (
                  <CachedIcon sx={{ fontSize: 14, color: colors.gray }} />
                )}
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Stack>
      <CustomText
        text={value || '—'}
        fontSize={15}
        fontWeight={600}
        sx={rtl ? { direction: 'rtl', textAlign: 'left' } : undefined}
      />
    </Stack>
  );
}

// One part of the card, drawn the way the product page draws a section: a
// heading band carrying the part's name, a word on how much it holds, and every
// control the part has gathered at the right-hand end - with the part's own
// content inside the same box beneath it.
//
// The box is what the card was missing. A band marked where a part began and
// nothing marked where it ended, so ten of them down one column read as a
// single scroll and a reviewer halfway down had to remember which heading they
// were still under. The band is drawn as a heading rather than as a tinted
// stripe for the same reason: the card behind it is white and the page behind
// that is the pale blue the band is, so tint alone said nothing.
//
// A folded part keeps no padding, or an empty box would sit under its heading
// looking like content that failed to arrive.
function CardSection({
  title,
  subtitle,
  actions,
  collapsed = false,
  onToggle,
  children,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  collapsed?: boolean;
  // Pressing the band folds the part away and pressing it again brings it back.
  // It was an eye among the controls before, which put the one thing done to
  // every part of the card in the row where each part keeps the things done
  // only to it - and made the largest target on the heading do nothing.
  onToggle?: () => void;
  children?: ReactNode;
}) {
  return (
    <Box
      sx={{
        mt: 2.5,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: colors.white,
        // The same shadow the product page gives its sections. On a white card
        // a hairline border is nearly the card itself; the lift is what makes
        // the box read as a box.
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        px={1.75}
        py={1.25}
        onClick={onToggle}
        sx={{
          backgroundColor: colors.topBackground,
          borderBottom: `1px solid ${colors.border}`,
          ...(onToggle ? { cursor: 'pointer', userSelect: 'none' } : {}),
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          {/* Which way the part is, said by the one thing on the heading that
              is not a control: a band that can be pressed and shows nothing for
              it is a band nobody presses. */}
          {onToggle ? (
            <ExpandMoreIcon
              sx={{
                fontSize: 20,
                color: colors.gray,
                transition: 'transform 150ms',
                transform: collapsed ? 'rotate(-90deg)' : 'none',
              }}
            />
          ) : null}
          <CustomText text={title} fontSize={16} fontWeight={600} color={colors.primary} />
          {subtitle ? <CustomText text={subtitle} fontSize={13} color={colors.gray} /> : null}
        </Stack>

        {/* Every control in the band drawn the way the product page draws them:
            a square button on the card's own white rather than a bare glyph, so
            a row of them reads as controls and keeps its shape whatever it
            holds. Set here rather than on each one, since the alternative is
            the same five lines repeated at twenty call sites. */}
        <Stack
          direction="row"
          alignItems="center"
          gap={0.75}
          // A control does what it says and nothing else: pressed here, it
          // would also fold the part it was meant to act on.
          onClick={(event) => event.stopPropagation()}
          sx={{
            flexShrink: 0,
            '& .MuiIconButton-root': {
              backgroundColor: colors.white,
              width: 30,
              height: 30,
              borderRadius: '4px',
            },
          }}
        >
          {actions}
        </Stack>
      </Stack>

      <Box sx={{ p: collapsed ? 0 : 1.75 }}>{children}</Box>
    </Box>
  );
}

// Closes a modal when its save has actually finished rather than when the
// button was pressed. Pressed-and-closed meant the Save button never showed it
// was working: the modal was gone before the request left, so a reviewer on a
// slow connection had nothing telling them the edit had been taken, and the
// only way to find out was to watch the row behind it change.
function useCloseWhenSaved(saving: boolean, close: () => void) {
  const started = useRef(false);

  useEffect(() => {
    if (saving) {
      started.current = true;
      return;
    }

    if (started.current) {
      started.current = false;
      close();
    }
  }, [saving, close]);
}

// Four to a row, on a grid rather than a wrapping flex line: with flex, a long
// value widens its own column and shunts the rest along, so the labels sat in
// different places on every card and none of them could be read down a column.
// Fixed columns keep every card's fields in the same spot. Aligned to the top,
// so a value needing two lines grows downwards instead of shoving its own label
// up out of the row.
function FieldGrid({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
        },
        columnGap: 4,
        rowGap: 2,
        alignItems: 'start',
        width: '100%',
      }}
    >
      {children}
    </Box>
  );
}

// The order they are read in rather than the order they are stored: what the
// thing is called, how much of it there is, where it came from, what it costs.
const INFO_FIELD_ORDER: InfoFieldName[] = [
  'nameEn',
  'nameAr',
  'weight',
  'drainedWeight',
  'estimatedWeight',
  'weightUnit',
  'countryOfManufactureISO',
  'countryOfRegistrationISO',
  'price',
];

// The photos a pass will be asked to read, ticked by a reviewer. It belongs to
// the part it sits in rather than to the card: the strip at the top is what the
// product looks like, and this is a choice about one reading - which photos show
// what is in the box, which ones carry the directions.
//
// Every photo is a full-size image sent at full detail, and a suggestion is
// mostly the same face of the pack shot over again, so the ticks are what a
// reviewer has instead of paying for all of them. All ticked is the starting
// state and means the whole strip - the same instruction as none ticked, which
// is why unticking the last one comes back here rather than leaving the pass
// blind. The picture itself still opens the carousel, so choosing does not mean
// squinting at a thumbnail.
function PhotoPicker({
  images,
  alt,
  isPicked,
  onToggle,
  onOpen,
  disabled,
}: {
  images: IPhotoSize[];
  alt: string;
  isPicked: (mediaId: string) => boolean;
  onToggle: (mediaId: string) => void;
  onOpen: (index: number) => void;
  disabled?: boolean;
}) {
  if (images.length === 0) return null;

  return (
    <Stack direction="row" spacing={1} mb={1.5} sx={{ overflowX: 'auto', pb: 0.5 }}>
      {images.map((image, index) => (
        <Box
          key={`picker-${image.id ?? image.medium ?? index}`}
          sx={{ position: 'relative', flex: '0 0 auto' }}
        >
          <Box
            component="img"
            src={image.medium ?? image.large ?? ''}
            alt={alt}
            loading="lazy"
            onClick={() => onOpen(index)}
            sx={{
              width: 72,
              height: 72,
              display: 'block',
              objectFit: 'cover',
              borderRadius: 1,
              border: `1px solid ${colors.border}`,
              cursor: 'pointer',
              // A photo the pass will not look at is dimmed as well as unticked:
              // the row is read at a glance to see how many are in, and a small
              // empty box is not visible at that distance.
              opacity: image.id && !isPicked(image.id) ? 0.35 : 1,
            }}
          />
          {image.id && (
            <Tooltip title={isPicked(image.id) ? 'Read this photo' : 'Skip this photo'}>
              <Box sx={{ position: 'absolute', top: 0, left: 0 }}>
                <Checkbox
                  size="small"
                  checked={isPicked(image.id)}
                  disabled={disabled}
                  onChange={() => onToggle(image.id as string)}
                  sx={{
                    padding: '2px',
                    margin: '2px',
                    backgroundColor: colors.white,
                    borderRadius: 1,
                    border: `1px solid ${colors.border}`,
                    '&:hover': { backgroundColor: colors.white },
                  }}
                />
              </Box>
            </Tooltip>
          )}
        </Box>
      ))}
    </Stack>
  );
}

// The picked set after a tick, and the two ways of saying "all of them" kept as
// one. Unticking the last photo would leave a pass with nothing to read, and a
// set covering every photo is the same instruction as no set at all - so both
// come back as empty.
function togglePickedPhoto(picked: string[], allMediaIds: string[], mediaId: string): string[] {
  const base = picked.length === 0 ? allMediaIds : picked;
  const next = base.includes(mediaId)
    ? base.filter((id) => id !== mediaId)
    : allMediaIds.filter((id) => base.includes(id) || id === mediaId);

  return next.length === allMediaIds.length ? [] : next;
}

// The panel as the pack prints it, one ingredient per line. The INCI leads
// because it is what a search matches and what every other product on the shelf
// is filed under; the pack's own wording follows it wherever the two differ,
// since that is the string a reviewer is checking against the photograph and the
// one the INCI throws away - "Green illite clay" becomes "Illite".
//
// Aliases and components are the two things a panel's brackets do - another name
// for the same substance, and the substances it is made of - and they are shown
// apart because keeping them apart is the whole reason the column has both.
function IngredientLine({ ingredient }: { ingredient: RawProductIngredient }) {
  const asPrinted =
    ingredient.asPrinted && ingredient.asPrinted !== ingredient.name ? ingredient.asPrinted : null;

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1,
        backgroundColor: colors.card,
        borderLeft: `3px solid ${colors.pureBorder}`,
      }}
    >
      <CustomText text={ingredient.name || '—'} fontSize={14} fontWeight={600} />
      {asPrinted && <CustomText text={asPrinted} fontSize={13} color={colors.gray} />}
      {ingredient.aliases?.length > 0 && (
        <CustomText
          text={`Also called: ${ingredient.aliases.join(', ')}`}
          fontSize={13}
          color={colors.gray}
        />
      )}
      {ingredient.components?.length > 0 && (
        <CustomText
          text={`Made of: ${ingredient.components.join(', ')}`}
          fontSize={13}
          color={colors.gray}
        />
      )}
    </Box>
  );
}

// One procedure as a person performs it: the written instructions, one line per
// step, in both languages side by side. The steps the pass also stores are the
// same sequence for a machine, and a reviewer checking a plan is checking the
// sentences somebody will read.
// The two languages of one list, kept apart rather than paired off in one
// string: this part is read the way the description, the contents and the
// warnings are read - English down the left, Arabic down the right, and the eye
// runs down one language at a time.
function english(names: RawProductAttributeName[] | undefined): string[] {
  return (names ?? []).map((name) => name.en);
}

function arabic(names: RawProductAttributeName[] | undefined): string[] {
  return (names ?? []).map((name) => name.ar);
}

// The eight lists the attributes pass fills, in the order a reviewer reads them:
// who the product is for, when it is used, what it acts on, what it promises,
// and what it says about its own formula. A list nobody filled is not shown at
// all - most packs assert only one or two of these, and eight headings with
// dashes beside them would bury the ones that were answered.
// Each list under the name a shopper will read on the product page, in the
// language of the box it heads: the English column says Targets, the Arabic one
// says يستهدف. The grey heading above them is gone with it - it said the same
// thing a third time, in a third wording, and these are the words that ship.
const ATTRIBUTE_LISTS: {
  en: string;
  ar: string;
  part: AttributePart;
  // Which list this is, for the pencil beside it: the pass that reads it
  // answers several of these at once, so the pass is not what a reviewer is
  // editing.
  section: AttributeSection;
  read: (attributes: RawProductAttributes) => RawProductAttributeName[];
}[] = [
  { en: 'Hair Types', ar: 'أنواع الشعر', part: 'types', section: 'hairTypes', read: (a) => a.suitability?.hairTypes ?? [] },
  { en: 'Skin Types', ar: 'أنواع البشرة', part: 'types', section: 'skinTypes', read: (a) => a.suitability?.skinTypes ?? [] },
  { en: 'Scalp Types', ar: 'أنواع فروة الرأس', part: 'types', section: 'scalpTypes', read: (a) => a.suitability?.scalpTypes ?? [] },
  { en: 'Beard Types', ar: 'أنواع اللحية', part: 'types', section: 'beardTypes', read: (a) => a.suitability?.beardTypes ?? [] },
  {
    en: 'Best Time To Use',
    ar: 'أفضل وقت للاستخدام',
    part: 'timings',
    section: 'usageTimings',
    read: (a) => a.suitability?.usageTimings ?? [],
  },
  { en: 'Targets', ar: 'يستهدف', part: 'concerns', section: 'concerns', read: (a) => a.claims?.concerns ?? [] },
  { en: 'Expected Results', ar: 'النتائج المتوقعة', part: 'goals', section: 'goals', read: (a) => a.claims?.goals ?? [] },
  { en: 'Formula Claims', ar: 'خصائص التركيبة', part: 'formula-claims', section: 'formulaClaims', read: (a) => a.formulaClaims ?? [] },
];

// How much this pass answered, counted the way the part is read: every name in
// every list, the scent and the flavour as one each, and one for each colour
// the product comes in.
function attributeCount(attributes: RawProductAttributes | null | undefined): number {
  if (!attributes) return 0;

  const named = ATTRIBUTE_LISTS.reduce((total, list) => total + list.read(attributes).length, 0);

  return (
    named +
    (attributes.scent ? 1 : 0) +
    (attributes.flavor ? 1 : 0) +
    (attributes.colors?.length ?? 0)
  );
}

// The scent as one line: the family a filter runs on, then the pack's own name
// for it, which is usually the loudest words on the front.
function scentLine(scent: RawProductScent, language: 'en' | 'ar'): string {
  return [scent.family?.[language], scent.name?.[language]]
    .filter((value): value is string => Boolean(value))
    .join(' · ');
}

// One entry per colour the product comes in, its parts separated so they stay
// legible. The code has no Arabic - it is an identifier - so it is shown on the
// English side, where the rest of the shade's English is.
function colorLine(color: RawProductShade, language: 'en' | 'ar'): string {
  return [
    color.family?.[language],
    color.undertone?.[language],
    color.shade?.[language],
    language === 'en' ? color.code : null,
  ]
    .filter((value): value is string => Boolean(value))
    .join(' · ');
}

function UsageInstructions({ usage }: { usage: RawProductUsage }) {
  const en = usage.instructions?.en ?? [];
  const ar = usage.instructions?.ar ?? [];
  const rows = Math.max(en.length, ar.length);

  // What the procedure is for, said once above the steps: the same sentence
  // repeated inside every step is how a plan stops being read at all.
  const meta = [usage.area, usage.frequency, usage.reapplication]
    .filter((value) => value && value !== 'not specified' && value !== 'not_specified')
    .join(' · ');

  return (
    <Box mb={1.5}>
      {/* The title in both languages, on the same two columns the steps below it
          use, so the heading sits over its own language rather than over both. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          columnGap: 2,
        }}
      >
        <CustomText text={usage.name?.en || 'Unnamed procedure'} fontSize={15} fontWeight={600} />
        <Box sx={{ direction: 'rtl', textAlign: 'right' }}>
          <CustomText text={usage.name?.ar || '—'} fontSize={15} fontWeight={600} />
        </Box>
      </Box>
      {meta && (
        <Box mb={0.5}>
          <CustomText text={meta} fontSize={13} color={colors.gray} />
        </Box>
      )}
      {/* The parts this procedure refers to, by the label's own markers, so a
          step saying "bottle 2" has something to be checked against. */}
      {usage.components?.length > 0 && (
        <Box mb={0.5}>
          <CustomText
            text={usage.components
              .map((component) => `${component.id}. ${component.name} (${component.amount})`)
              .join('  ·  ')}
            fontSize={13}
            color={colors.gray}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          columnGap: 2,
          rowGap: 1,
          alignItems: 'stretch',
        }}
      >
        {Array.from({ length: rows }, (_value, index) => (
          <Fragment key={`usage-step-${index}`}>
            <LanguageBox label={`EN ${index + 1}`} text={en[index] ?? null} />
            <LanguageBox label={`AR ${index + 1}`} text={ar[index] ?? null} rtl />
          </Fragment>
        ))}
      </Box>
    </Box>
  );
}

// One language's text in a box of its own, the same box a content is shown in:
// the two parts ask the same thing of a reviewer - read these two side by side
// and see where they disagree - so they are not going to look different while
// doing it.
//
// It fills the height it is given, so a long sentence on one side does not leave
// its translation floating half a box above it.
function LanguageBox({
  label,
  text,
  rtl,
  action,
  onEdit,
}: {
  label: string;
  text: string | null;
  rtl?: boolean;
  // Something to do to what this box is showing, sitting beside its title -
  // which is where a reviewer looks for it, the title being the name of the
  // thing they want done again.
  action?: React.ReactNode;
  // The box itself opens the editor where it is given one: the wrong answer is
  // what the reviewer is already pointing at, and the pencil beside the title
  // is a small thing to have to hit. The controls stop the press, so a reload
  // stays a reload.
  onEdit?: () => void;
}) {
  return (
    <Box
      onClick={onEdit}
      sx={{
        height: '100%',
        p: 1.25,
        borderRadius: 1,
        backgroundColor: colors.card,
        [rtl ? 'borderRight' : 'borderLeft']: `3px solid ${colors.pureBorder}`,
        direction: rtl ? 'rtl' : 'ltr',
        textAlign: rtl ? 'right' : 'left',
        cursor: onEdit ? 'pointer' : 'default',
        transition: 'background-color 120ms, border-color 120ms',
        ...(onEdit
          ? {
              '&:hover': {
                backgroundColor: colors.inputBackground,
                [rtl ? 'borderRightColor' : 'borderLeftColor']: colors.primary,
              },
            }
          : {}),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <CustomText text={label} fontSize={12} fontWeight={700} color={colors.gray} />
        {action ? (
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            {action}
          </Box>
        ) : null}
      </Stack>
      <Box mt={0.25}>
        <CustomText text={text || '—'} fontSize={15} />
      </Box>
    </Box>
  );
}

// A list that came back empty, in one line rather than in the pair of boxes a
// filled one gets.
//
// It is here rather than left out, because a pass that answered nothing and a
// pass nobody ran look the same on a card that hides both. It is one line
// because eleven of these at the height of a filled row is a screen of dashes,
// and what a reviewer wants from an empty list is to see that it is empty and
// to have the two controls for doing something about it.
function EmptyAttributeRow({
  label,
  action,
  onEdit,
}: {
  label: string;
  action?: React.ReactNode;
  onEdit?: () => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      onClick={onEdit}
      sx={{
        px: 1.25,
        py: 0.25,
        borderRadius: 1,
        backgroundColor: colors.card,
        cursor: onEdit ? 'pointer' : 'default',
        transition: 'background-color 120ms',
        ...(onEdit ? { '&:hover': { backgroundColor: colors.inputBackground } } : {}),
      }}
    >
      <CustomText text={label} fontSize={12} fontWeight={700} color={colors.gray} />
      <CustomText text="—" fontSize={13} color={colors.lightgray} />
      {action ? (
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{ display: 'inline-flex', alignItems: 'center' }}
        >
          {action}
        </Box>
      ) : null}
    </Stack>
  );
}

// One thing out of the pack, in one language. The count leads the name because
// it is what a reviewer is checking against the box in front of them, and it is
// left off entirely where the pack printed none: a "1" nobody printed reads as a
// fact off the label and is the one mistake this list can make that a reviewer
// would not catch.
//
// It fills the height it is given rather than the height of its own words. The
// two languages are laid out as a grid of pairs, so the taller of a pair sets
// the row and the shorter has to grow into it - otherwise every row after the
// first is offset from its own translation by whatever the last one differed by,
// which is the drift the side-by-side layout exists to remove.
function ContentEntryCard({
  entry,
  rtl,
}: {
  entry: RawProductContentEntry | null;
  rtl?: boolean;
}) {
  // The pack's two readings can name a different number of things, which is
  // itself worth seeing: the missing half is left as an empty cell holding the
  // row open rather than closed up, so what follows stays beside its pair.
  if (!entry) return <Box />;

  return (
    <Box
      sx={{
        height: '100%',
        p: 1.25,
        borderRadius: 1,
        backgroundColor: colors.card,
        [rtl ? 'borderRight' : 'borderLeft']: `3px solid ${colors.pureBorder}`,
        direction: rtl ? 'rtl' : 'ltr',
        textAlign: rtl ? 'right' : 'left',
      }}
    >
      <CustomText
        text={
          entry.amount === null
            ? entry.contentName
            : `${entry.amount} ${entry.amountUnit} - ${entry.contentName}`
        }
        fontSize={15}
        fontWeight={600}
      />
      {entry.analysis && (
        <Box mt={0.25}>
          <CustomText text={entry.analysis} fontSize={14} color={colors.gray} />
        </Box>
      )}
    </Box>
  );
}

// The paragraph each language opens with, in the same shape as an entry so the
// two introductions share a row too.
function ContentsIntroduction({
  language,
  rtl,
}: {
  language: RawProductContentsLanguage | null;
  rtl?: boolean;
}) {
  return (
    <Box
      sx={{
        height: '100%',
        direction: rtl ? 'rtl' : 'ltr',
        textAlign: rtl ? 'right' : 'left',
      }}
    >
      <CustomText text={rtl ? 'AR' : 'EN'} fontSize={12} fontWeight={700} color={colors.gray} />
      {language?.introduction && (
        <Box mt={0.5}>
          <CustomText text={language.introduction} fontSize={14} color={colors.gray} />
        </Box>
      )}
    </Box>
  );
}

// The two readings zipped by position, which is the only thing that can pair
// them: they are written in one call and in the same order, and neither carries
// an id to match the other by. A language that named one thing fewer leaves an
// empty cell rather than shifting everything below it up a row.
function contentEntryPairs(
  en: RawProductContentsLanguage | null,
  ar: RawProductContentsLanguage | null,
): Array<[RawProductContentEntry | null, RawProductContentEntry | null]> {
  const enEntries = en?.contents ?? [];
  const arEntries = ar?.contents ?? [];
  const rows = Math.max(enEntries.length, arEntries.length);

  return Array.from({ length: rows }, (_value, index) => [
    enEntries[index] ?? null,
    arEntries[index] ?? null,
  ]);
}

// A country is stored as its code and shown by its name, since a reviewer
// checking a pack against the card is reading a name off the pack, not a code.
function displayInfo(
  field: InfoFieldName,
  value: string | number | null,
  countries?: CountryOption[],
): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (field !== 'countryOfManufactureISO' && field !== 'countryOfRegistrationISO') {
    return String(value);
  }

  const country = countries?.find((option) => option.iso === value);
  return country ? country.name : String(value);
}

export default function RawProductReviewCard({
  item,
  leafCategories,
  isConfirming,
  isPromoting,
  isRunningMissing,
  isReextracting,
  isDeletingSuggestion,
  isRedescribing,
  isSavingIdentity,
  isSavingInfo,
  isReextractingInfo,
  isReextractingBrand,
  brandOptions,
  isEditingBrand,
  isSavingBrand,
  onEditBrand,
  onCloseBrandEditor,
  onSaveBrand,
  isReextractingContents,
  isReextractingUsages,
  isSavingIngredients,
  isReextractingWarnings,
  isReextractingAttributes,
  isRankingIngredients,
  isRerunningPipeline,
  editingField,
  editingInfoField,
  isEditingCategory,
  identityValues,
  countries,
  identity,
  info,
  brand,
  description,
  contents,
  usages,
  ingredients,
  rankedIngredients,
  warnings,
  attributes,
  readiness,
  avatarMediaId: savedAvatarMediaId,
  isSettingAvatar,
  rerunningStep,
  onConfirmCategory,
  onPromote,
  onRunMissing,
  onReextractOcr,
  onRedescribe,
  onEditDescription,
  isEditingDescription,
  isSavingDescription,
  onCloseDescriptionEditor,
  onSaveDescription,
  isSavingUsages,
  onSaveUsages,
  isSavingContents,
  onSaveContents,
  isSavingWarnings,
  onSaveWarnings,
  isSavingAttributes,
  onSaveAttributes,
  onEditField,
  onCloseFieldEditor,
  onSaveIdentity,
  onEditInfoField,
  onCloseInfoFieldEditor,
  onSaveInfo,
  onReextractInfo,
  onReextractBrand,
  onReextractContents,
  onReextractUsages,
  onSaveIngredients,
  onReextractWarnings,
  onReextractAttributes,
  onRankIngredients,
  onRerunPipeline,
  onEditCategory,
  onCloseCategoryEditor,
  onDeleteSuggestion,
  onDeleteImage,
  onSetAvatar,
  onRerunStep,
}: Props) {
  const [showOcr, setShowOcr] = useState(false);
  // The long lists stay folded away the way the label text is: what is in the
  // box, how it is used, a panel of forty substances, that panel again in two
  // languages, and the cautions under it push everything a reviewer reads on
  // every row off the screen. Each one is asked for when it is being checked,
  // and its photo picker folds with it, since ticking photos is something you
  // only do while looking at what they produced.
  //
  // Open from the start are the short parts read on every row: the front photo,
  // the description, what the product is, and what it claims about itself.
  const [showAvatar, setShowAvatar] = useState(true);
  const [showDescription, setShowDescription] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [showBrand, setShowBrand] = useState(false);
  const [showWhatItIs, setShowWhatItIs] = useState(true);
  const [showAttributes, setShowAttributes] = useState(true);
  const [showContents, setShowContents] = useState(false);
  const [showUsages, setShowUsages] = useState(false);
  const [showIngredients, setShowIngredients] = useState(false);
  const [showRankedIngredients, setShowRankedIngredients] = useState(false);
  const [showWarnings, setShowWarnings] = useState(false);
  // The photos ticked for the next contents reading. Held on the card and sent
  // with the request rather than stored: which photos show what is in the box is
  // a judgement about this reading, and there is nothing for a later run to
  // inherit from it. Empty means all of them.
  const [pickedContentsMediaIds, setPickedContentsMediaIds] = useState<string[]>([]);
  // The usages pass has its own ticks: the photos that show what is in the box
  // and the photos that carry the directions are rarely the same ones.
  const [pickedUsagesMediaIds, setPickedUsagesMediaIds] = useState<string[]>([]);
  // And its own again for the panel: the photograph that shows the ingredients
  // is a third face of the pack, and this pass cuts the panel out of whatever it
  // is handed - so a strip of pack fronts is worse than useless to it.
  const [pickedIngredientsMediaIds, setPickedIngredientsMediaIds] = useState<string[]>([]);
  // Deleting takes the raw product down with the suggestion, and everything read
  // off the pack goes with it - so the click is asked about rather than obeyed.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Which photo is being asked about. A stray photo from another product is
  // worth removing, but the one the avatar points at is what a created product
  // is pictured by - and nothing in the app puts a deleted photo back.
  const [confirmingImageId, setConfirmingImageId] = useState<string | null>(null);
  const [choosingRunMissing, setChoosingRunMissing] = useState(false);
  // Held on the card rather than on the page: nothing outside this card needs to
  // know, and what is being edited is the list this card is showing.
  const [isEditingUsages, setIsEditingUsages] = useState(false);
  const [isEditingContents, setIsEditingContents] = useState(false);
  const [isEditingWarnings, setIsEditingWarnings] = useState(false);
  const [isEditingAttributes, setIsEditingAttributes] = useState(false);
  // Which part of the claims the modal was opened on, or null for all of them:
  // the pencil on the heading edits everything, the pencil beside a row edits
  // that row. Either way Save writes the whole set.
  const [editingAttributeSection, setEditingAttributeSection] = useState<AttributeSection | null>(null);
  // Whether the next reading of what this pack claims is taken from the
  // description instead of the label. The pass does that by itself for a pack
  // that claims nothing at all; this is for the pack that claims plenty and
  // claims nothing worth keeping - three ways of saying skin care - which
  // nobody but the reviewer reading them can tell. One switch for the whole
  // part, since it is the same question whichever control is pressed after it.
  const [attributesFromDescription, setAttributesFromDescription] = useState(false);
  useCloseWhenSaved(isSavingAttributes, () => setIsEditingAttributes(false));
  useCloseWhenSaved(isSavingUsages, () => setIsEditingUsages(false));
  useCloseWhenSaved(isSavingContents, () => setIsEditingContents(false));
  useCloseWhenSaved(isSavingWarnings, () => setIsEditingWarnings(false));
  const [isEditingIngredients, setIsEditingIngredients] = useState(false);
  useCloseWhenSaved(isSavingIngredients, () => setIsEditingIngredients(false));
  const [carouselIndex, setCarouselIndex] = useState<number | null>(null);

  const rawProductId = item.rawProductId;

  // A save on this card wins over what the list was loaded with, so the fields
  // show what the product now holds without refetching the queue under a
  // reviewer who is mid-read.
  const tagEn = identity ? identity.tagEn : item.tagEn;
  const tagAr = identity ? identity.tagAr : item.tagAr;
  const kind = identity ? identity.kind : item.kind;
  const targetAreas = identity ? identity.targetAreas : item.targetAreas ?? [];
  const targetArea = identity ? identity.targetArea : item.targetArea;
  const targetAudience = identity ? identity.targetAudience : item.targetAudience;
  const targetGender = identity ? identity.targetGender : item.targetGender;
  const form = identity ? identity.form : item.form;
  const packShape = identity ? identity.packShape : item.packShape;
  // The leaf the category pass just picked, the same way: it comes back with
  // the rest of what that run settled, and reading it off the row instead left
  // a card showing the tag the run wrote beside the leaf it replaced.
  const leafCategory =
    identity && 'leafCategory' in identity ? identity.leafCategory : item.leafCategory;

  const infoValues = {
    nameEn: info ? info.nameEn : item.nameEn,
    nameAr: info ? info.nameAr : item.nameAr,
    weight: info ? info.weight : item.weight,
    drainedWeight: info ? info.drainedWeight : item.drainedWeight,
    estimatedWeight: info ? info.estimatedWeight : item.estimatedWeight,
    weightUnit: info ? info.weightUnit : item.weightUnit,
    countryOfManufactureISO: info ? info.countryOfManufactureISO : item.countryOfManufactureISO,
    countryOfRegistrationISO: info ? info.countryOfRegistrationISO : item.countryOfRegistrationISO,
    price: info ? info.price : item.price,
  };

  // A refresh's answer wins over what the list was loaded with, including when
  // that answer is null: a run that found the label says too little has to be
  // able to clear a sentence, or the card would go on showing one the row no
  // longer holds. Hence checking for the key rather than for a value.
  const hasBeenRedescribed = description !== undefined;
  const descriptionEn = hasBeenRedescribed ? description.descriptionEn : item.descriptionEn;
  const descriptionAr = hasBeenRedescribed ? description.descriptionAr : item.descriptionAr;

  // A refresh's answer wins over the list payload for the same reason as the
  // description, and with the same care about null: a run that finds the pack
  // holds nothing separate has to be able to clear a list the card is showing.
  const hasBeenRecontented = contents !== undefined;
  const contentsValue = hasBeenRecontented ? contents.contents : item.contents;
  const contentsCheckedAt = hasBeenRecontented
    ? contents.contentsCheckedAt
    : item.contentsCheckedAt;
  const contentsEn = contentsValue?.en ?? null;
  const contentsAr = contentsValue?.ar ?? null;

  // Which photos the pass will be asked to read. Empty is every one of them,
  // which is where the card starts and what unticking the last box returns to -
  // so an empty list shows as all boxes ticked rather than as none.
  const allMediaIds = item.images
    .map((image) => image.id)
    .filter((id): id is string => Boolean(id));
  const readsAllPhotos = pickedContentsMediaIds.length === 0;
  const isPickedForContents = (mediaId: string) =>
    readsAllPhotos || pickedContentsMediaIds.includes(mediaId);

  const toggleContentsMedia = (mediaId: string) =>
    setPickedContentsMediaIds((picked) => togglePickedPhoto(picked, allMediaIds, mediaId));

  // The same for the usages pass, kept apart from the contents ticks because
  // they answer different questions about the same strip.
  const usagesReadsAllPhotos = pickedUsagesMediaIds.length === 0;
  const isPickedForUsages = (mediaId: string) =>
    usagesReadsAllPhotos || pickedUsagesMediaIds.includes(mediaId);
  const toggleUsagesMedia = (mediaId: string) =>
    setPickedUsagesMediaIds((picked) => togglePickedPhoto(picked, allMediaIds, mediaId));

  // A refresh's answer wins over the list payload, and null with a stamp is a
  // real answer here too: a row the pass could not write for.
  const hasBeenReused = usages !== undefined;
  const usagesValue = hasBeenReused ? usages.usages : item.usages;
  const usagesCheckedAt = hasBeenReused ? usages.usagesCheckedAt : item.usagesCheckedAt;

  const ingredientsReadsAllPhotos = pickedIngredientsMediaIds.length === 0;
  const isPickedForIngredients = (mediaId: string) =>
    ingredientsReadsAllPhotos || pickedIngredientsMediaIds.includes(mediaId);
  const toggleIngredientsMedia = (mediaId: string) =>
    setPickedIngredientsMediaIds((picked) => togglePickedPhoto(picked, allMediaIds, mediaId));

  // Which one of the six attribute passes is being read again from a heading on
  // this card, and what the last one of them answered. One at a time: firing all
  // six is the whole re-read the reload exists to avoid.
  const [reloadingAttribute, setReloadingAttribute] = useState<AttributePart | null>(null);
  const [reloadedAttributes, setReloadedAttributes] = useState<
    RawProductAttributes | null | undefined
  >(undefined);

  // The local copy is dropped the moment the page hands down a newer answer.
  //
  // It is here because a reload of one heading answers to this card alone and
  // never reaches the page - so while it stands, it is the newest thing anybody
  // has. A save does reach the page, and without this the card went on showing
  // the reload's answer over the reviewer's own edit until the queue was
  // reloaded.
  useEffect(() => {
    if (attributes !== undefined) setReloadedAttributes(undefined);
  }, [attributes]);

  const reloadAttributePart = async (part: AttributePart) => {
    if (reloadingAttribute) return;
    setReloadingAttribute(part);

    try {
      const result = await reextractRawProductAttributePart({
        rawProductId,
        part,
        withDescription: attributesFromDescription,
      });
      setReloadedAttributes(result?.attributes ?? null);
    } finally {
      setReloadingAttribute(null);
    }
  };

  // The reload that sits at the end of a heading's row. Its tooltip names what
  // will change rather than the heading it sits on, because one pass answers
  // several headings - the four type lists are one question, and so are the
  // colour, the scent and the flavour.
  // The pencil beside one row, opening the modal on that row alone. Next to the
  // reload, because they are the two things to do to a list that is wrong: read
  // it off the pack again, or write it yourself.
  const openAttributeEditor = (section: AttributeSection) => () => {
    setEditingAttributeSection(section);
    setIsEditingAttributes(true);
  };

  const attributeEdit = (section: AttributeSection, what: string) => (
    <Tooltip title={`Edit the ${what}`}>
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <IconButton
          size="small"
          disabled={Boolean(reloadingAttribute) || isReextractingAttributes}
          onClick={openAttributeEditor(section)}
        >
          <EditOutlinedIcon sx={{ fontSize: 15, color: colors.gray }} />
        </IconButton>
      </span>
    </Tooltip>
  );

  const attributeReload = (part: AttributePart, note: string) => (
    <Tooltip title={reloadingAttribute === part ? 'Reading the pack…' : note}>
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <IconButton
          size="small"
          disabled={Boolean(reloadingAttribute) || isReextractingAttributes}
          onClick={() => reloadAttributePart(part)}
        >
          {reloadingAttribute === part ? (
            <CircularProgress size={13} />
          ) : (
            <CachedIcon sx={{ fontSize: 15, color: colors.gray }} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );

  const hasBeenReingredient = ingredients !== undefined;
  const ingredientsValue = hasBeenReingredient ? ingredients.ingredients : item.ingredients;
  const ingredientsCheckedAt = hasBeenReingredient
    ? ingredients.ingredientsCheckedAt
    : item.ingredientsCheckedAt;

  // What the pack prints as cautions. Empty with a date is the ordinary answer -
  // most packs print none - and it is a different fact from nobody having asked.
  const hasBeenRewarned = warnings !== undefined;
  const hasBeenReattributed = attributes !== undefined;
  // A heading reloaded from this card answers with the whole column, and it is
  // newer than anything the page is holding - it was written to the row a moment
  // ago.
  const attributesValue = reloadedAttributes !== undefined
    ? reloadedAttributes
    : hasBeenReattributed
      ? attributes.attributes
      : item.attributes;
  const attributesCheckedAt = hasBeenReattributed
    ? attributes.attributesCheckedAt
    : item.attributesCheckedAt;
  const warningsValue = hasBeenRewarned ? warnings.warnings : item.warnings;
  const warningsCheckedAt = hasBeenRewarned ? warnings.warningsCheckedAt : item.warningsCheckedAt;

  // The ordered, named panel. A transcription that has just been re-read makes
  // the stored order stale - it was built from the rows the old reading held -
  // so a fresh panel clears it and the reviewer is told to rank again rather
  // than shown an order that no longer matches the list beneath it.
  const hasBeenRanked = rankedIngredients !== undefined;
  const rankedValue = hasBeenRanked
    ? rankedIngredients.rankedIngredients
    : hasBeenReingredient
      ? null
      : item.rankedIngredients;
  const rankedCheckedAt = hasBeenRanked
    ? rankedIngredients.rankedIngredientsCheckedAt
    : hasBeenReingredient
      ? null
      : item.rankedIngredientsCheckedAt;

  // A write can be the last thing a row was waiting for, so the stage chip, the
  // promote button and the missing-info list follow the newest answer rather
  // than the list payload, which was computed before any of it happened.
  const avatarMediaId = savedAvatarMediaId ?? item.avatarMediaId;
  const stage = readiness?.stage ?? item.stage;
  const blockers = readiness?.blockers ?? item.blockers;

  // A re-read's answer wins over the list payload, null included: a run that
  // finds the pack names no brand has to be able to clear the one shown.
  const hasBeenRebranded = brand !== undefined;
  const brandValue = hasBeenRebranded ? brand.brand : item.brand;
  const brandCheckedAt = hasBeenRebranded ? brand.brandCheckedAt : item.brandCheckedAt;

  const title = infoValues.nameEn || 'Unnamed product';
  // Read off the row once: inside a handler the property is a value that could
  // have changed, and the guard around the line that shows it does not reach in
  // there.
  const barcode = item.barcode ?? '';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderColor: colors.border,
        borderRadius: 2,
        // Lifted off the page the way a section on the product page is, so a
        // row in a queue of them reads as one card rather than as where the
        // scrolling happens to be.
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CustomText text={title} fontSize={20} fontWeight={700} />
          {/* What this row is waiting for, in one word. The queue is thousands of
              rows deep and a reviewer scrolling it needs to know which ones they
              can act on without reading the whole card first. */}
          {stage === 'ready' ? (
            <Chip size="small" color="success" label="Ready" />
          ) : stage === 'needs_category' ? (
            <Chip size="small" color="warning" label="Needs a category" />
          ) : (
            <Chip size="small" label="Incomplete" />
          )}
        </Stack>
        {/* The photos this used to link out for are on the card itself, in the
            strip below. Pressing it copies it: a barcode on this card is read
            to be pasted somewhere else - a search, a supplier's site - and
            reading a thirteen-digit number off the screen by eye is how one
            digit gets transposed. */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {barcode ? (
            <Tooltip title="Copy the barcode">
              <Box
                onClick={async () => {
                  const copied = await copyToClipboard(barcode);
                  snackbarService.showSnackbar(
                    copied ? 'Barcode copied' : 'Could not copy the barcode',
                    copied ? 'success' : 'error',
                  );
                }}
                sx={{ cursor: 'pointer' }}
              >
                <CustomText text={barcode} fontSize={14} color={colors.gray} />
              </Box>
            </Tooltip>
          ) : null}
          {/* Every section still unread, in one press. Up here because a fresh
              card is where it is needed and the header is what is read first;
              each section keeps its own reload for the answer that is there but
              wrong. Gone once nothing is missing or the row is already a product. */}
          {!item.productId && blockers.length > 0 && (
            <Tooltip title="Read every section that has not been read yet">
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={isRunningMissing}
                  onClick={() => setChoosingRunMissing(true)}
                >
                  {isRunningMissing ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CachedIcon sx={{ fontSize: 16, color: colors.gray }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* The front photo, chosen here and nowhere else. Above the description
          because it is the first thing read: the description was written from
          these photos, and a strip showing another product's pack says the
          whole card is about to be wrong. Photos also settle what the label
          text cannot - texture, applicator, size. */}
      <CardSection
        title="Avatar"
        collapsed={!showAvatar}
        onToggle={() => setShowAvatar((shown) => !shown)}
      >
        <Collapse in={showAvatar}>
          {item.images.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
              {item.images.map((image, index) => (
                <Box key={image.id ?? image.medium ?? index} sx={{ position: 'relative', flex: '0 0 auto' }}>
                  <Box
                    component="img"
                    src={image.medium ?? image.large ?? ''}
                    alt={title}
                    loading="lazy"
                    onClick={() => setCarouselIndex(index)}
                    sx={{
                      width: 96,
                      height: 96,
                      display: 'block',
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: `1px solid ${colors.border}`,
                      cursor: 'pointer',
                    }}
                  />
                  {/* One stray photo from another product does not make the whole
                      suggestion bad, so each image can go on its own. */}
                  {image.id && item.suggestionId && (
                    <IconButton
                      size="small"
                      onClick={() => setConfirmingImageId(image.id as string)}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        padding: '2px',
                        backgroundColor: colors.white,
                        border: `1px solid ${colors.border}`,
                        '&:hover': { backgroundColor: colors.white },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14, color: colors.delete }} />
                    </IconButton>
                  )}
                  {/* Which photo shows the front. Marked in place rather than by
                      reordering the strip: a reviewer fixing a bad pick is looking
                      at the wrong photo beside the right one, and a strip that
                      resorted itself on every save would move the picture out from
                      under the click that changed it. Filled and green on the one
                      in use, hollow and grey on the rest - so a row with no front
                      chosen at all reads as no green anywhere. */}
                  {image.id && (
                    <Tooltip title={image.id === avatarMediaId ? 'The front photo' : 'Use as the front photo'}>
                      {/* The wrapper carries the positioning, not the button inside
                          it. Tooltip anchors to whatever it holds, and a span left in
                          the normal flow around an absolutely-positioned button is a
                          zero-size box at the corner of the thumbnail - so the
                          message appeared nowhere near the thing it describes. */}
                      <Box
                        sx={{
                          display: 'inline-flex',
                          position: 'absolute',
                          top: 30,
                          right: 2,
                        }}
                      >
                        <IconButton
                          size="small"
                          disabled={isSettingAvatar || image.id === avatarMediaId}
                          onClick={() => onSetAvatar(rawProductId, image.id as string)}
                          sx={{
                            padding: '2px',
                            backgroundColor: colors.white,
                            border: `1px solid ${colors.border}`,
                            '&:hover': { backgroundColor: colors.white },
                            // Disabled on the one already chosen, since pressing it
                            // would ask for what is already true - but it is the one
                            // that most needs to stay legible, so it keeps its
                            // colour instead of greying out with the others.
                            '&.Mui-disabled': { backgroundColor: colors.white },
                          }}
                        >
                          {image.id === avatarMediaId ? (
                            <CheckCircleIcon sx={{ fontSize: 14, color: colors.success }} />
                          ) : (
                            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: colors.gray }} />
                          )}
                        </IconButton>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Stack>
          )}
          {item.images.length === 0 && (
            <CustomText text="No photos on this suggestion." fontSize={14} color={colors.gray} />
          )}
        </Collapse>
      </CardSection>

      {/* Under the photos and above everything judged, because of where it
          comes from rather than what it says: every value below was settled by
          a pass that was handed the answers of the passes before it, so a wrong
          one early is repeated by the rest and the card reads as agreeing with
          itself. This was written from the label and the photos alone. Read it
          first: if it names a product the fields below do not, one of them is
          wrong, and nothing else on this card would have told you so. */}
      <CardSection
        title="Description"
        // Nothing here is a list, so there is nothing to count: the heading
        // says whether the part has been answered at all, and the part itself
        // says what with.
        subtitle={descriptionEn || descriptionAr ? 'done' : undefined}
        collapsed={!showDescription}
        onToggle={() => setShowDescription((shown) => !shown)}
        actions={
          <>
            {/* Both sentences at once rather than a pencil on each: they are read
                against each other, so a correction to one is made looking at the
                other. */}
            <Tooltip title="Edit this description">
              <span style={{ display: 'inline-flex' }}>
                <IconButton size="small" onClick={() => onEditDescription(rawProductId)}>
                  <EditOutlinedIcon sx={{ fontSize: 16, color: colors.gray }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Write this description again">
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={isRedescribing}
                  onClick={() => onRedescribe(rawProductId)}
                >
                  {isRedescribing ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CachedIcon sx={{ fontSize: 16, color: colors.gray }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Collapse in={showDescription}>
          {/* The two sentences beside each other in the same boxes the contents use,
              English on the left and Arabic on the right: the Arabic is not a
              translation of the English but a second reading of the same pack, and
              where the two disagree about what the product is it is the disagreement
              that has to be visible. One above the other reads as a sentence and its
              restatement, which is the comparison nobody makes. */}
          {descriptionEn || descriptionAr ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 2,
                rowGap: 1,
                alignItems: 'stretch',
              }}
            >
              <LanguageBox label="EN" text={descriptionEn} />
              <LanguageBox label="AR" text={descriptionAr} rtl />
            </Box>
          ) : (
            <CustomText
              text={
                isRedescribing
                  ? 'Reading the label and photos…'
                  : hasBeenRedescribed
                    ? 'The label and photos did not say enough to describe this product.'
                    : 'No description yet — use the refresh control to write one.'
              }
              fontSize={14}
              color={colors.gray}
            />
          )}
        </Collapse>
      </CardSection>

      <ImageCarousel
        open={carouselIndex !== null}
        images={item.images}
        initialIndex={carouselIndex ?? 0}
        onClose={() => setCarouselIndex(null)}
      />

      <InfoFieldModal
        field={editingInfoField}
        productName={title}
        info={infoValues}
        identityValues={identityValues}
        countries={countries}
        isSaving={isSavingInfo}
        onClose={onCloseInfoFieldEditor}
        onSave={(patch: RawProductInfoPatch) => onSaveInfo(rawProductId, patch)}
      />

      <AttributesModal
        attributes={attributesValue ?? null}
        rawProductId={rawProductId}
        open={isEditingAttributes}
        productName={title}
        isSaving={isSavingAttributes}
        section={editingAttributeSection}
        onClose={() => setIsEditingAttributes(false)}
        onSave={(next) => onSaveAttributes(rawProductId, next)}
      />

      <WarningsModal
        warnings={warningsValue ?? null}
        open={isEditingWarnings}
        productName={title}
        isSaving={isSavingWarnings}
        onClose={() => setIsEditingWarnings(false)}
        onSave={(next) => onSaveWarnings(rawProductId, next)}
      />

      <IngredientsModal
        rawProductId={rawProductId}
        mediaIds={pickedIngredientsMediaIds}
        ingredients={ingredientsValue ?? null}
        open={isEditingIngredients}
        productName={title}
        isSaving={isSavingIngredients}
        onClose={() => setIsEditingIngredients(false)}
        onSave={(next) => onSaveIngredients(rawProductId, next)}
      />

      <ContentsModal
        contents={contentsValue ?? null}
        open={isEditingContents}
        productName={title}
        isSaving={isSavingContents}
        onClose={() => setIsEditingContents(false)}
        onSave={(patch) => onSaveContents(rawProductId, patch)}
      />

      <UsageModal
        usages={usagesValue ?? null}
        open={isEditingUsages}
        productName={title}
        isSaving={isSavingUsages}
        onClose={() => setIsEditingUsages(false)}
        onSave={(edits) => onSaveUsages(rawProductId, edits)}
      />

      <DescriptionModal
        rawProductId={isEditingDescription ? rawProductId : null}
        productName={title}
        descriptionEn={descriptionEn}
        descriptionAr={descriptionAr}
        isSaving={isSavingDescription}
        onClose={onCloseDescriptionEditor}
        onSave={(patch: RawProductDescriptionPatch) => onSaveDescription(rawProductId, patch)}
      />

      <IdentityFieldModal
        field={editingField}
        productName={title}
        identity={{ tagEn, tagAr, kind, targetAreas, targetAudience, targetGender, form, packShape }}
        identityValues={identityValues}
        isSaving={isSavingIdentity}
        onClose={onCloseFieldEditor}
        onSave={(patch: RawProductIdentityPatch) => onSaveIdentity(rawProductId, patch)}
      />

      <BrandModal
        open={isEditingBrand}
        productName={title}
        current={brandValue}
        brands={brandOptions}
        isSaving={isSavingBrand}
        onClose={onCloseBrandEditor}
        onSave={(patch) => onSaveBrand(rawProductId, patch)}
      />

      <CategoryPickerModal
        open={isEditingCategory}
        productName={title}
        current={leafCategory}
        candidates={item.candidates}
        leafCategories={leafCategories}
        isSaving={isConfirming}
        onClose={onCloseCategoryEditor}
        onSave={(leafCategoryId) => onConfirmCategory(rawProductId, leafCategoryId)}
      />

      {/* What a product will be built out of, shown before anything is decided
          about it: these are the pack's own particulars, and a reviewer reads
          them off the photos in front of them rather than judging them. */}
      <CardSection
        title="Basic Information"
        subtitle={
          INFO_FIELD_ORDER.some((field) => infoValues[field] !== null && infoValues[field] !== undefined)
            ? 'done'
            : undefined
        }
        collapsed={!showInfo}
        onToggle={() => setShowInfo((shown) => !shown)}
        actions={
          <>
            {/* The batch never returns to a row it has already read - the timestamp
                is set on the first attempt - so without this control a bad reading
                can only be corrected field by field, by hand. */}
            <Tooltip title="Read these off the pack again">
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={isReextractingInfo}
                  onClick={() => onReextractInfo(rawProductId)}
                >
                  {isReextractingInfo ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CachedIcon sx={{ fontSize: 16, color: colors.gray }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Collapse in={showInfo}>
          <FieldGrid>
            {INFO_FIELD_ORDER.map((field) => (
              <Field
                key={field}
                label={INFO_FIELDS[field].label}
                value={displayInfo(field, infoValues[field], countries)}
                rtl={field === 'nameAr'}
                onEdit={() => onEditInfoField(rawProductId, field)}
              />
            ))}
          </FieldGrid>
        </Collapse>
      </CardSection>

      {/* Who is behind the pack. Only the brand for now; the companies a pack
          names - who makes it, who imports it, who distributes it - join as rows
          of this part rather than as parts of their own. */}
      <CardSection
        title="Brand & companies"
        subtitle={brandValue ? 'done' : undefined}
        collapsed={!showBrand}
        onToggle={() => setShowBrand((shown) => !shown)}
        actions={
          <>
            <Tooltip title="Edit the brand">
              <span style={{ display: 'inline-flex' }}>
                <IconButton size="small" onClick={() => onEditBrand(rawProductId)}>
                  <EditOutlinedIcon sx={{ fontSize: 16, color: colors.gray }} />
                </IconButton>
              </span>
            </Tooltip>
            {/* The batch never returns to a row it has already read, so a pack
                filed under the wrong brand is corrected from here. */}
            <Tooltip title="Read the brand off the pack again">
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={isReextractingBrand}
                  onClick={() => onReextractBrand(rawProductId)}
                >
                  {isReextractingBrand ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CachedIcon sx={{ fontSize: 16, color: colors.gray }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Collapse in={showBrand}>
          {brandValue ? (
            <FieldGrid>
              <Field label="Brand (EN)" value={brandValue.nameEn} />
              <Field label="Brand (AR)" value={brandValue.nameAr} rtl />
              {/* Every spelling the brand has been matched on. A spelling here that
                  belongs to another brand is how a wrong merge shows itself. */}
              <Field label="Spellings seen" value={brandValue.aliases.join(' · ') || null} />
            </FieldGrid>
          ) : (
            <CustomText
              text={
                isReextractingBrand
                  ? 'Reading the brand off the pack…'
                  : brandCheckedAt
                    ? 'The pack names no brand.'
                    : 'No brand yet — use the refresh control to read one.'
              }
              fontSize={14}
              color={colors.gray}
            />
          )}
        </Collapse>
      </CardSection>

      {/* Below the particulars, because none of them decide any of this: every
          value here was judged from the pack rather than read off it, and the
          leaf at the end of the row is what they were all judged for. */}
      <CardSection
        title="What it is"
        // The tag in both languages and a leaf to file it under: the rest of
        // this part is what those two were decided from, and a card with either
        // of them missing is not one anybody can promote.
        subtitle={tagEn && tagAr && leafCategory ? 'done' : undefined}
        collapsed={!showWhatItIs}
        onToggle={() => setShowWhatItIs((shown) => !shown)}
        actions={
          <>
            {/* All six passes, in the order they depend on each other - the category
                one is handed the answers of the five before it. A single re-run,
                because re-reading one of them against the others' stale answers is
                how a card ends up agreeing with itself and being wrong. */}
            <Tooltip title="Read kind, area, audience, form, shape and category again">
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={isRerunningPipeline}
                  onClick={() => onRerunPipeline(rawProductId)}
                >
                  {isRerunningPipeline ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CachedIcon sx={{ fontSize: 16, color: colors.gray }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Collapse in={showWhatItIs}>
          <FieldGrid>
            {/* First, because it is what the leaf search was actually run against -
                every field after it is a value the tag was built from or checked
                against. Both languages side by side: the Arabic is the half that
                goes wrong, rendering a stage of life or a form as a looser word
                while the English still reads correctly, and the disagreement is
                only visible with the two together. */}
            <Field
              label="Tag (EN)"
              value={tagEn}
              onEdit={() => onEditField(rawProductId, 'tagEn')}
              onRerun={() => onRerunStep(rawProductId, 'category')}
              isRerunning={rerunningStep === 'category'}
            />
            <Field
              label="Tag (AR)"
              value={tagAr}
              rtl
              onEdit={() => onEditField(rawProductId, 'tagAr')}
              onRerun={() => onRerunStep(rawProductId, 'category')}
              isRerunning={rerunningStep === 'category'}
            />
            <Field
              label="Kind"
              value={kind}
              onEdit={() => onEditField(rawProductId, 'kind')}
              onRerun={() => onRerunStep(rawProductId, 'kind')}
              isRerunning={rerunningStep === 'kind'}
            />
            {/* The leading area, which is the one the leaf is decided by. The rest
                the pack named are behind the pencil, where the order they are in is
                the answer. */}
            <Field
              label="Target area"
              value={targetArea}
              onEdit={() => onEditField(rawProductId, 'targetAreas')}
              onRerun={() => onRerunStep(rawProductId, 'area')}
              isRerunning={rerunningStep === 'area'}
            />
            <Field
              label="Target audience"
              value={targetAudience}
              onEdit={() => onEditField(rawProductId, 'targetAudience')}
              onRerun={() => onRerunStep(rawProductId, 'audience')}
              isRerunning={rerunningStep === 'audience'}
            />
            {/* Beside the audience because one call answers both, and it is the
                answer the Arabic is addressed from. Empty means the pack sells to
                anybody, which the Arabic writes in the masculine. */}
            <Field
              label="Gender"
              value={targetGender}
              onEdit={() => onEditField(rawProductId, 'targetGender')}
              onRerun={() => onRerunStep(rawProductId, 'audience')}
              isRerunning={rerunningStep === 'audience'}
            />
            <Field
              label="Form"
              value={form}
              onEdit={() => onEditField(rawProductId, 'form')}
              onRerun={() => onRerunStep(rawProductId, 'form')}
              isRerunning={rerunningStep === 'form'}
            />
            {/* Beside the form because the two are read together and confused for
                each other: this is what the pack does, the form is what is in it. */}
            <Field
              label="Pack shape"
              value={packShape}
              onEdit={() => onEditField(rawProductId, 'packShape')}
              onRerun={() => onRerunStep(rawProductId, 'packShape')}
              isRerunning={rerunningStep === 'packShape'}
            />

            {/* Where the product is filed. The graph writes it and the pencil
                corrects it - there is no separate proposal to approve, because
                approving one was never an act anybody performed on its own:
                promoting the product is where a reviewer says yes to everything on
                the card, this included. */}
            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.25} alignItems="center">
                <CustomText text="Graph picked" fontSize={13} color={colors.gray} />
                <Tooltip title="Pick the category again">
                  <Box sx={{ display: 'inline-flex' }}>
                    <IconButton
                      size="small"
                      disabled={rerunningStep === 'category'}
                      onClick={() => onRerunStep(rawProductId, 'category')}
                      sx={{ padding: '1px' }}
                    >
                      {rerunningStep === 'category' ? (
                        <CircularProgress size={12} />
                      ) : (
                        <CachedIcon sx={{ fontSize: 14, color: colors.gray }} />
                      )}
                    </IconButton>
                  </Box>
                </Tooltip>
                <Tooltip title="Choose the category">
                  <IconButton
                    size="small"
                    onClick={() => onEditCategory(rawProductId)}
                    sx={{ padding: '1px' }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 14, color: colors.gray }} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <CustomText text={leafCategory?.name || '—'} fontSize={15} fontWeight={600} />
            </Stack>
          </FieldGrid>
        </Collapse>
      </CardSection>

      {/* What the pack asserts about itself, directly under what it is because
          the two are one question asked twice over: the row above says which
          product this is, and these lists say which one of that kind - who it
          is for, what it promises, what its formula is held to. Every name here
          arrived with the printed phrase it was read from, and one that could
          not be found on the label was dropped before it got this far. */}
      <CardSection
        title="Attributes"
        subtitle={
          attributesCheckedAt || attributesValue
            ? `${attributeCount(attributesValue)} answered`
            : undefined
        }
        collapsed={!showAttributes}
        onToggle={() => setShowAttributes((shown) => !shown)}
        actions={
          <>
            <Tooltip title="Edit every one of these">
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditingAttributeSection(null);
                    setIsEditingAttributes(true);
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16, color: colors.gray }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Read what the pack claims, off the stored label text">
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={isReextractingAttributes}
                  onClick={() => onReextractAttributes(rawProductId, attributesFromDescription)}
                >
                  {isReextractingAttributes ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CachedIcon sx={{ fontSize: 16, color: colors.gray }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Collapse in={showAttributes}>
          {/* Above the lists rather than among the controls in the heading: it
              is read with the names it would change, and it applies to whatever
              is pressed next - the whole pass, or one list on its own. */}
          <Stack
            direction="row"
            alignItems="center"
            mb={1}
            onClick={() => setAttributesFromDescription((from) => !from)}
            sx={{ cursor: 'pointer', userSelect: 'none', width: 'fit-content' }}
          >
            <Checkbox
              size="small"
              checked={attributesFromDescription}
              onChange={(event) => setAttributesFromDescription(event.target.checked)}
              sx={{ p: 0.5 }}
            />
            <CustomText text="With Description" fontSize={13} color={colors.gray} />
          </Stack>
          {attributesValue ? (
            <Stack spacing={1.25}>
              {ATTRIBUTE_LISTS.map(({ en, ar, part, section, read }) => {
                // An empty list is shown rather than left out. A pass that
                // answered nothing and a pass nobody has run look the same on a
                // card that hides both, and the reviewer correcting this product
                // is the one who has to tell them apart.
                const names = read(attributesValue);
                const controls = (
                  <>
                    {attributeEdit(section, en.toLowerCase())}
                    {attributeReload(part, `Read the ${en.toLowerCase()} off the pack again`)}
                  </>
                );

                if (!names.length) {
                  return (
                    <EmptyAttributeRow
                      key={en}
                      label={`${en} · ${ar}`}
                      action={controls}
                      onEdit={openAttributeEditor(section)}
                    />
                  );
                }

                return (
                  /* English left, Arabic right, in the same two columns the
                     description and the warnings use - each box headed in its own
                     language, since these are the words the product page will use.
                     The reload for the pass behind this row sits at its end. */
                  <Box
                    key={en}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      columnGap: 2,
                      rowGap: 0.5,
                      alignItems: 'stretch',
                    }}
                  >
                    <LanguageBox
                      label={en}
                      text={english(names).join('  ·  ')}
                      action={controls}
                      onEdit={openAttributeEditor(section)}
                    />
                    <LanguageBox
                      label={ar}
                      text={arabic(names).join('  ·  ')}
                      rtl
                      onEdit={openAttributeEditor(section)}
                    />
                  </Box>
                );
              })}

              {attributesValue.scent ? (
                <Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      columnGap: 2,
                      rowGap: 0.5,
                      alignItems: 'stretch',
                    }}
                  >
                    <LanguageBox
                      label="Scent"
                      text={scentLine(attributesValue.scent, 'en')}
                      action={
                        <>
                          {attributeEdit('scent', 'scent')}
                          {attributeReload('appearance', 'Read the colour, scent and flavour off the pack again')}
                        </>
                      }
                      onEdit={openAttributeEditor('scent')}
                    />
                    <LanguageBox
                      label="الرائحة"
                      text={scentLine(attributesValue.scent, 'ar')}
                      rtl
                      onEdit={openAttributeEditor('scent')}
                    />
                  </Box>
                </Box>
              ) : (
                <EmptyAttributeRow
                  label="Scent · الرائحة"
                  action={
                    <>
                      {attributeEdit('scent', 'scent')}
                      {attributeReload('appearance', 'Read the colour, scent and flavour off the pack again')}
                    </>
                  }
                  onEdit={openAttributeEditor('scent')}
                />
              )}

              {/* Beside the scent and never folded into it: a taste is what a
                  toothpaste is chosen by, and a smell is not. */}
              {attributesValue.flavor ? (
                <Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      columnGap: 2,
                      rowGap: 0.5,
                      alignItems: 'stretch',
                    }}
                  >
                    <LanguageBox
                      label="Flavour"
                      text={scentLine(attributesValue.flavor, 'en')}
                      action={
                        <>
                          {attributeEdit('flavor', 'flavour')}
                          {attributeReload('appearance', 'Read the colour, scent and flavour off the pack again')}
                        </>
                      }
                      onEdit={openAttributeEditor('flavor')}
                    />
                    <LanguageBox
                      label="النكهة"
                      text={scentLine(attributesValue.flavor, 'ar')}
                      rtl
                      onEdit={openAttributeEditor('flavor')}
                    />
                  </Box>
                </Box>
              ) : (
                <EmptyAttributeRow
                  label="Flavour · النكهة"
                  action={
                    <>
                      {attributeEdit('flavor', 'flavour')}
                      {attributeReload('appearance', 'Read the colour, scent and flavour off the pack again')}
                    </>
                  }
                  onEdit={openAttributeEditor('flavor')}
                />
              )}

              {(attributesValue.colors ?? []).length ? (
                <Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      columnGap: 2,
                      rowGap: 0.5,
                      alignItems: 'stretch',
                    }}
                  >
                    {(attributesValue.colors ?? []).map((color, index) => (
                      <Fragment key={`color-${index}`}>
                        {/* One reload for the whole colour list: a palette is read
                            in one answer, so it is offered beside the first of them. */}
                        <LanguageBox
                          label={`Colour ${index + 1}`}
                          text={colorLine(color, 'en')}
                          action={
                            index === 0 ? (
                              <>
                                {attributeEdit('colors', 'colours')}
                                {attributeReload('appearance', 'Read the colour, scent and flavour off the pack again')}
                              </>
                            ) : undefined
                          }
                          onEdit={openAttributeEditor('colors')}
                        />
                        <LanguageBox
                          label={`اللون ${index + 1}`}
                          text={colorLine(color, 'ar')}
                          rtl
                          onEdit={openAttributeEditor('colors')}
                        />
                      </Fragment>
                    ))}
                  </Box>
                </Box>
              ) : (
                <EmptyAttributeRow
                  label="Colour · اللون"
                  action={
                    <>
                      {attributeEdit('colors', 'colours')}
                      {attributeReload('appearance', 'Read the colour, scent and flavour off the pack again')}
                    </>
                  }
                  onEdit={openAttributeEditor('colors')}
                />
              )}
            </Stack>
          ) : (
            <CustomText
              text={
                isReextractingAttributes
                  ? 'Reading what the pack claims…'
                  : attributesCheckedAt
                    ? 'This pack asserts nothing about who it is for or what it does.'
                    : 'Not read yet - use the refresh control to read what the pack claims.'
              }
              fontSize={14}
              color={colors.gray}
            />
          )}
        </Collapse>
      </CardSection>

      {/* What the buyer takes out of the box, below what the product is because
          it is a different question about the same pack: everything above says
          what the thing is, this says how many separate things there are. Never
          the ingredients - an ingredient is what the product is made of, a
          content is a thing you can pick up - and never the net weight, which
          measures the substance rather than counting anything. */}
      <CardSection
        title="Product contents"
        subtitle={
          contentsCheckedAt || contentsEn?.contents?.length
            ? `${contentsEn?.contents?.length ?? 0} inside`
            : undefined
        }
        collapsed={!showContents}
        onToggle={() => setShowContents((shown) => !shown)}
        actions={
          <>
            {/* Both languages and every item at once: they name the same things in
                the same order, so an item is added or dropped from both together. */}
            <Tooltip title="Edit what the pack holds">
              <span style={{ display: 'inline-flex' }}>
                <IconButton size="small" onClick={() => setIsEditingContents(true)}>
                  <EditOutlinedIcon sx={{ fontSize: 16, color: colors.gray }} />
                </IconButton>
              </span>
            </Tooltip>
            {/* The batch will not come back to a row it has already stamped - that
                stamp is what stops every empty pack being paid for twice - so this
                is the only way to ask a pack again. */}
            <Tooltip
              title={
                readsAllPhotos
                  ? 'Read what is in the pack again, from every photo'
                  : `Read what is in the pack again, from the ${pickedContentsMediaIds.length} ticked photos`
              }
            >
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={isReextractingContents}
                  onClick={() => onReextractContents(rawProductId, pickedContentsMediaIds)}
                >
                  {isReextractingContents ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CachedIcon sx={{ fontSize: 16, color: colors.gray }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Collapse in={showContents}>
          <PhotoPicker
            images={item.images}
            alt={title}
            isPicked={isPickedForContents}
            onToggle={toggleContentsMedia}
            onOpen={setCarouselIndex}
            disabled={isReextractingContents}
          />

          {contentsEn?.contents?.length ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 2,
                rowGap: 1,
                alignItems: 'stretch',
              }}
            >
              <ContentsIntroduction language={contentsEn} />
              <ContentsIntroduction language={contentsAr} rtl />
              {contentEntryPairs(contentsEn, contentsAr).map(([enEntry, arEntry], index) => (
                <Fragment key={`contents-row-${index}`}>
                  <ContentEntryCard entry={enEntry} />
                  <ContentEntryCard entry={arEntry} rtl />
                </Fragment>
              ))}
            </Box>
          ) : (
            <CustomText
              text={
                isReextractingContents
                  ? 'Reading what is in the pack…'
                  : contentsCheckedAt
                    ? 'This pack holds nothing separate - one product in its own container.'
                    : 'Not read yet - use the refresh control to read what is in the pack.'
              }
              fontSize={14}
              color={colors.gray}
            />
          )}
        </Collapse>
      </CardSection>

      {/* Under the contents because it is written from them: the parts a step
          refers to are the things the contents pass named, so a kit whose parts
          were read wrong has a plan that was never going to be right. A wrong
          plan is usually a contents refresh first and this one second. */}
      <CardSection
        title="Product usages"
        subtitle={
          usagesCheckedAt || usagesValue?.length
            ? `${usagesValue?.length ?? 0} written`
            : undefined
        }
        collapsed={!showUsages}
        onToggle={() => setShowUsages((shown) => !shown)}
        actions={
          <>
            {/* Every procedure at once: the answer a save sends is the whole list,
                which is what lets a removed procedure say what it means. */}
            <Tooltip title="Edit these usages">
              <span style={{ display: 'inline-flex' }}>
                <IconButton size="small" onClick={() => setIsEditingUsages(true)}>
                  <EditOutlinedIcon sx={{ fontSize: 16, color: colors.gray }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                usagesReadsAllPhotos
                  ? 'Write how this is used again, from every photo'
                  : `Write how this is used again, from the ${pickedUsagesMediaIds.length} ticked photos`
              }
            >
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={isReextractingUsages}
                  onClick={() => onReextractUsages(rawProductId, pickedUsagesMediaIds)}
                >
                  {isReextractingUsages ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CachedIcon sx={{ fontSize: 16, color: colors.gray }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Collapse in={showUsages}>
          <PhotoPicker
            images={item.images}
            alt={title}
            isPicked={isPickedForUsages}
            onToggle={toggleUsagesMedia}
            onOpen={setCarouselIndex}
            disabled={isReextractingUsages}
          />

          {usagesValue?.length ? (
            usagesValue.map((usage, index) => (
              <UsageInstructions key={`${usage.name?.en ?? 'usage'}-${index}`} usage={usage} />
            ))
          ) : (
            <CustomText
              text={
                isReextractingUsages
                  ? 'Writing how this is used…'
                  : usagesCheckedAt
                    ? 'The label and photos did not say enough to write how this is used.'
                    : 'Not written yet - use the refresh control to write how this is used.'
              }
              fontSize={14}
              color={colors.gray}
            />
          )}
        </Collapse>
      </CardSection>

      {/* The panel, below the two parts that are written and this one that is
          only copied: what a product is made of, in the pack's own order, with
          nothing added and nothing explained. It is the one part on this card
          where a sentence of the model's own is a defect. */}
      <CardSection
        title="Product ingredients"
        subtitle={
          ingredientsCheckedAt || ingredientsValue?.length
            ? `${ingredientsValue?.length ?? 0} declared`
            : undefined
        }
        collapsed={!showIngredients}
        onToggle={() => setShowIngredients((shown) => !shown)}
        actions={
          <>
            <Tooltip
              title={
                ingredientsReadsAllPhotos
                  ? 'Read the ingredients panel again, from every photo, and check it before saving'
                  : `Read the ingredients panel again, from the ${pickedIngredientsMediaIds.length} ticked photos, and check it before saving`
              }
            >
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={isSavingIngredients}
                  onClick={() => setIsEditingIngredients(true)}
                >
                  {isSavingIngredients ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CachedIcon sx={{ fontSize: 16, color: colors.gray }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Collapse in={showIngredients}>
          <PhotoPicker
            images={item.images}
            alt={title}
            isPicked={isPickedForIngredients}
            onToggle={toggleIngredientsMedia}
            onOpen={setCarouselIndex}
            disabled={isSavingIngredients}
          />

          {ingredientsValue?.length ? (
            // A panel runs to forty substances and is read down rather than across,
            // so it is a column of narrow lines that wrap rather than a grid: the
            // order is the pack's own and it is the one thing a reviewer checks.
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 2,
                rowGap: 1,
              }}
            >
              {ingredientsValue.map((ingredient, index) => (
                <IngredientLine key={`${ingredient.name}-${index}`} ingredient={ingredient} />
              ))}
            </Box>
          ) : (
            <CustomText
              text={
                ingredientsCheckedAt
                    ? 'No panel could be read off this pack.'
                    : 'Not read yet - use the refresh control to read the ingredients panel.'
              }
              fontSize={14}
              color={colors.gray}
            />
          )}
        </Collapse>
      </CardSection>

      {/* The same panel again, ordered and named, as a part of its own rather
          than folded into the one above it. They are two passes and two
          answers - one copies the label, the other judges what the substances
          are doing here - and shown together in one part a reviewer could not
          tell which list they were looking at. This one is built from the list
          above and never from the label, so re-reading the panel is what comes
          first when a row here is wrong. */}
      <CardSection
        title="Ordered ingredients"
        subtitle={
          rankedValue?.length
            ? `${rankedValue.length} ordered`
            : // A pack with no panel has nothing to order, so this one is
              // answered rather than waiting - a blank beside the heading
              // reads as a pass that has not run.
              ingredientsCheckedAt && !ingredientsValue?.length
              ? '0 ordered'
              : undefined
        }
        collapsed={!showRankedIngredients}
        onToggle={() => setShowRankedIngredients((shown) => !shown)}
        actions={
          <>
            {/* Disabled until there is a panel to order: this pass reads the
                transcription above, and a pack without one is waiting for that pass
                rather than for this. */}
            <Tooltip
              title={
                ingredientsValue?.length
                  ? 'Order the panel by what explains the product, and name every row in Arabic'
                  : 'Read the ingredients panel first - there is nothing to order yet'
              }
            >
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={isRankingIngredients || !ingredientsValue?.length}
                  onClick={() => onRankIngredients(rawProductId)}
                >
                  {isRankingIngredients ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CachedIcon sx={{ fontSize: 16, color: colors.gray }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Collapse in={showRankedIngredients}>
          {rankedValue?.length ? (
            // The same pair of boxes the description, the contents and the usage
            // steps use: English on the left, Arabic on the right, each row a pair
            // that begins on the same line. The number is carried in the label, so
            // the order this pass exists to write is readable down either column.
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 2,
                rowGap: 1,
                alignItems: 'stretch',
              }}
            >
              {rankedValue.map((ingredient, index) => (
                <Fragment key={`${ingredient.en}-${index}`}>
                  <LanguageBox label={`EN ${index + 1}`} text={ingredient.en} />
                  <LanguageBox label={`AR ${index + 1}`} text={ingredient.ar} rtl />
                </Fragment>
              ))}
            </Box>
          ) : (
            <CustomText
              text={
                isRankingIngredients
                  ? 'Ordering the panel and naming it in Arabic…'
                  : !ingredientsValue?.length
                    ? 'Nothing to order - the ingredients panel has not been read.'
                    : rankedCheckedAt
                      ? 'No order was written for this panel.'
                      : 'Not ordered yet - use the refresh control to write the order and the Arabic names.'
              }
              fontSize={14}
              color={colors.gray}
            />
          )}
        </Collapse>
      </CardSection>

      {/* The pack's own cautions, kept apart from the usages: a step is
          performed in order, a warning is true of the whole product however it
          is used, and a reader looks for it before they start. This pass copies
          and never invents, so an empty list is a fact about the pack rather
          than a gap - most packs print nothing at all. */}
      <CardSection
        title="Warnings"
        subtitle={
          warningsCheckedAt || warningsValue?.en?.length
            ? `${warningsValue?.en?.length ?? 0} printed`
            : undefined
        }
        collapsed={!showWarnings}
        onToggle={() => setShowWarnings((shown) => !shown)}
        actions={
          <>
            {/* Both languages at once: a caution and its Arabic are one line, and
                dropping one side alone is how the two lists stop lining up. */}
            <Tooltip title="Edit these warnings">
              <span style={{ display: 'inline-flex' }}>
                <IconButton size="small" onClick={() => setIsEditingWarnings(true)}>
                  <EditOutlinedIcon sx={{ fontSize: 16, color: colors.gray }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Read the cautions the pack prints, off the stored label text">
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={isReextractingWarnings}
                  onClick={() => onReextractWarnings(rawProductId)}
                >
                  {isReextractingWarnings ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CachedIcon sx={{ fontSize: 16, color: colors.gray }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Collapse in={showWarnings}>
          {warningsValue?.en?.length ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 2,
                rowGap: 1,
                alignItems: 'stretch',
              }}
            >
              {warningsValue.en.map((warning, index) => (
                <Fragment key={`${warning}-${index}`}>
                  <LanguageBox label={`EN ${index + 1}`} text={warning} />
                  <LanguageBox label={`AR ${index + 1}`} text={warningsValue.ar?.[index] ?? ''} rtl />
                </Fragment>
              ))}
            </Box>
          ) : (
            <CustomText
              text={
                isReextractingWarnings
                  ? 'Reading the warnings off the pack…'
                  : warningsCheckedAt
                    ? 'This pack prints no warnings.'
                    : 'Not read yet - use the refresh control to read the pack\'s warnings.'
              }
              fontSize={14}
              color={colors.gray}
            />
          )}
        </Collapse>

        {/* Set apart from the fields above it like every other section, because
            it is not one of them: those are values somebody read off the pack,
            this is the pack's own words they were read from. */}
      </CardSection>
      {/* The OCR is long and only needed when the other fields are not enough
          to decide, so it stays folded away until asked for. */}
      <CardSection
        title="Label text"
        collapsed={!showOcr}
        onToggle={() => setShowOcr((shown) => !shown)}
        actions={
          <>
            {/* Beside the text it replaces, because that is where the reason to
                press it appears: when the words on show are plainly a bad scan, no
                amount of re-deciding helps - every later pass is reading these
                same words. */}
            <Tooltip title="Read the label off the photos again">
              <span style={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={isReextracting}
                  onClick={() => onReextractOcr(rawProductId)}
                >
                  {isReextracting ? (
                    <CircularProgress size={14} />
                  ) : (
                    <CachedIcon sx={{ fontSize: 16, color: colors.gray }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      >
        <Collapse in={showOcr}>
          <Box
            mt={0.5}
            p={1.5}
            sx={{
              backgroundColor: colors.card,
              borderRadius: 1,
              maxHeight: 180,
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              fontSize: 13,
              lineHeight: 1.6,
              color: colors.text,
            }}
          >
            {item.ocrText || '—'}
          </Box>
        </Collapse>
      </CardSection>

      {/* The two ends a row can come to, side by side and given equal weight,
          because they are the only things on the card that finish it: one makes
          the product, the other says there was never a product here. Everything
          above them is reading and correcting.

          Promote is enabled only when nothing is missing - and what is missing
          is named directly beneath it, so a greyed-out button is never a
          mystery. */}
      <Stack direction="row" spacing={2} mt={2.5}>
        <Box sx={{ flex: '1 1 0' }}>
          <CustomButton
            fullWidth
            text="Delete Suggestion"
            variant="outlined"
            intent="delete"
            height={44}
            isLoading={isDeletingSuggestion}
            disabled={!item.suggestionId}
            onClick={() => setConfirmingDelete(true)}
          />
        </Box>
        <Box sx={{ flex: '1 1 0' }}>
          {/* A row that has already become a product has nothing left to promote,
              so the button is the way to the product it became rather than an
              offer to make a second one. */}
          {item.productId ? (
            <CustomButton
              fullWidth
              text="Open the product"
              variant="outlined"
              height={44}
              onClick={() => window.open(`/products/all-products/${item.productId}`, '_blank')}
            />
          ) : (
            <CustomButton
              fullWidth
              text={stage === 'ready' ? 'Promote to product' : 'Not ready to promote'}
              height={44}
              isLoading={isPromoting}
              disabled={stage !== 'ready'}
              onClick={() => onPromote(rawProductId)}
            />
          )}
        </Box>
      </Stack>

      {/* What stands between this row and a product, directly under the button it
          disables - which is where a reviewer looks the moment they find they
          cannot press it. One field per line rather than a sentence: the list
          runs to a dozen entries on a fresh row, and read as prose none of them
          can be counted or matched to the field it names. */}
      {blockers.length > 0 && (
        <Box mt={1.5}>
          <CustomText text="Missing info" fontSize={14} fontWeight={700} color={colors.gray} />
          <Stack spacing={0.25} mt={0.5}>
            {blockers.map((blocker) => (
              <CustomText key={blocker} text={`- ${blocker}`} fontSize={14} color={colors.gray} />
            ))}
          </Stack>
        </Box>
      )}

      <CustomDialog
        open={Boolean(confirmingImageId)}
        title="Delete this photo?"
        message="It leaves the suggestion for good - nothing in the app puts it back. If it is the photo chosen as the front, the product will have none until the avatar pass runs again."
        buttons={[
          <CustomButton
            key="cancel"
            text="Cancel"
            variant="outlined"
            onClick={() => setConfirmingImageId(null)}
          />,
          <CustomButton
            key="delete"
            text="Delete"
            intent="delete"
            onClick={() => {
              const mediaId = confirmingImageId;
              setConfirmingImageId(null);
              if (mediaId && item.suggestionId) onDeleteImage(item.suggestionId, mediaId);
            }}
          />,
        ]}
      />

      <CustomDialog
        open={choosingRunMissing}
        title="Read the missing sections"
        message="Not every pack has a contents list. Leave it out to skip that read."
        buttons={[
          <CustomButton
            key="cancel"
            text="Cancel"
            variant="outlined"
            onClick={() => setChoosingRunMissing(false)}
          />,
          <CustomButton
            key="without"
            text="Without contents"
            variant="outlined"
            onClick={() => {
              setChoosingRunMissing(false);
              onRunMissing(rawProductId, false);
            }}
          />,
          <CustomButton
            key="with"
            text="With contents"
            onClick={() => {
              setChoosingRunMissing(false);
              onRunMissing(rawProductId, true);
            }}
          />,
        ]}
      />

      <CustomDialog
        open={confirmingDelete}
        title="Delete this suggestion?"
        message="Its raw product goes with it, and everything read off the pack - the label text, the category, the particulars - goes with that. The suggestion can be restored from the Deleted Suggestions page."
        buttons={[
          <CustomButton
            key="cancel"
            text="Cancel"
            variant="outlined"
            onClick={() => setConfirmingDelete(false)}
          />,
          <CustomButton
            key="delete"
            text="Delete"
            intent="delete"
            isLoading={isDeletingSuggestion}
            onClick={() => {
              setConfirmingDelete(false);
              if (item.suggestionId) onDeleteSuggestion(item.suggestionId);
            }}
          />,
        ]}
      />
    </Paper>
  );
}
